<template>
    <AppLayout title="Welcome">
        <div class="p-4 md:p-12">
            <div v-if="errors" class="bg-red-400 w-full my-5 p-4 rounded-xl shadow-lg">
                <p class="font-bold pb-2 last:pb-0" v-for="error in errors">{{ error[0] }}</p>
            </div>

            <CalendarView
                v-if="!isMobile"
                @changeCurrentDay="changeCurrentDay"
                @purchaseBlock="purchaseBlock"
                @viewSpace="viewSpaceDetails = $event"
                :spaces="spaces"
                :current-day="currentDay"
                :computed-calendar-view="computedCalendarView"
                :events="events"
                :define-hours="defineHours"
                :current-day-override="currentDayOverride"
                :opening-times="opening_times"
                :opening-time-overrides="opening_time_overrides"
            />

            <MobileCalendar
                v-else
                @changeCurrentDay="changeCurrentDay"
                @purchaseBlock="purchaseBlock"
                @viewSpace="viewSpaceDetails = $event"
                :spaces="spaces"
                :current-day="currentDay"
                :computed-calendar-view="computedCalendarView"
                :events="events"
                :define-hours="defineHours"
                :current-day-override="currentDayOverride"
                :opening-times="opening_times"
                :opening-time-overrides="opening_time_overrides"
            />
        </div>
    </AppLayout>

    <DialogModal v-if="!isMobile" @close="viewSpaceDetails = 0" closeable :show="viewSpaceDetails !== 0">
        <template #title>{{ viewSpaceDetails.name }}</template>
        <template #content>
            <div class="text-lg">Table Size: {{ viewSpaceDetails.size }}</div>
            <div class="mt-4 text-lg">Max Seats: {{ viewSpaceDetails.seats }}</div>
            <div v-if="spaceMinimumLabel" class="mt-4 text-lg">{{ spaceMinimumLabel }}</div>
            <template v-if="viewSpaceDetails.description">
                <div class="mt-4 font-semibold">About this table</div>
                <div class="mt-2" v-html="viewSpaceDetails.description" />
            </template>
        </template>
        <template #footer>
            <div class="flex gap-4">
                <PrimaryButton @click="viewSpaceDetails = 0">Close</PrimaryButton>
            </div>
        </template>
    </DialogModal>

    <DialogModal v-if="!isMobile" @close="closeSelectingDatesModal()" closeable :show="selectingDatesModal">
        <template #title>Make a booking?</template>
        <template #content>
            <div class="mb-2">
                <h3 class="text-md">
                    You are currently booking a table for:
                    <span class="font-bold">{{ selectedSpace.name }}</span>
                </h3>
                <h4 class="text-md">
                    Starting at:
                    <span class="font-bold">{{ formatDateTime(selectedDateTime().toISO()) }}</span>
                </h4>
                <h4 class="text-md">
                    Ending at:
                    <span class="font-bold">{{ formatDateTime(selectedDateTime(amountOfHours).toISO()) }}</span>
                </h4>

                <div class="flex gap-4">
                    <div class="text-md">Table Size: {{ selectedSpace.size }}</div>
                    <div class="text-md">Max Seats: {{ selectedSpace.seats }}</div>
                </div>
            </div>
            <p class="block mb-2 font-medium text-lg text-gray-700">
                How many hours?
                <span class="text-sm text-gray-500 font-normal">(Minimum: {{ minimumHours(selectedSpace) }})</span>
            </p>
            <div class="flex mb-4 gap-4 items-center">
                <PrimaryButton @click="addHoursToBooking(-1)" :disabled="amountOfHours <= minimumHours(selectedSpace)">
                    <i class="fa-solid fa-minus p-0.5" />
                </PrimaryButton>
                <div class="text-lg font-extrabold">{{ amountOfHours }} hour{{ amountOfHours !== 1 ? 's' : '' }}</div>
                <PrimaryButton @click="addHoursToBooking(1)" :disabled="!canAddMoreHours()">
                    <i class="fa-solid fa-plus p-0.5" />
                </PrimaryButton>
            </div>
            <div v-if="calculateBookingPrice()" class="text-2xl font-bold text-right">
                Price: £{{ calculateBookingPrice() }} (Inc. VAT)
            </div>
            <div v-else-if="amountOfHours >= 1" class="text-sm text-red-600 text-right">
                No pricing available for {{ amountOfHours }} hour{{ amountOfHours !== 1 ? 's' : '' }}. Please try a different duration.
            </div>
        </template>
        <template #footer>
            <div class="flex gap-4">
                <DangerButton @click="closeSelectingDatesModal()">Cancel</DangerButton>
                <PrimaryButton v-if="calculateBookingPrice()" @click="checkout()">Book</PrimaryButton>
            </div>
        </template>
    </DialogModal>

    <BookingSheet
        v-if="isMobile"
        :show="selectingDatesModal"
        :space="selectedSpace"
        :current-day="currentDay"
        :start-time="selectedTime"
        :hours="amountOfHours"
        :min-hours="minimumHours(selectedSpace)"
        :max-hours="maxBookableHours"
        :price-for="priceForHours"
        @update:hours="setBookingHours"
        @close="closeSelectingDatesModal()"
        @book="checkout()"
    />

    <SpaceSheet
        v-if="isMobile"
        :show="viewSpaceDetails !== 0"
        :space="viewSpaceDetails || null"
        @close="viewSpaceDetails = 0"
    />

    <SignInSheet
        v-if="isMobile"
        :show="showSignIn"
        :space="selectedSpace"
        :current-day="currentDay"
        :start-time="selectedTime"
        @close="closeSignInSheet()"
    />

    <SignInModal
        v-else
        :show="showSignIn"
        :space="selectedSpace"
        :current-day="currentDay"
        :start-time="selectedTime"
        @close="closeSignInSheet()"
    />
</template>

<script setup>
    import AppLayout from "../Layouts/BookingLayouts/AppLayout.vue";
    import PrimaryButton from "../InertiaComponents/PrimaryButton.vue";
    import CalendarView from "../Components/CalendarView.vue";
    import MobileCalendar from "../Components/Calendar/MobileCalendar.vue";
    import BookingSheet from "../Components/Calendar/BookingSheet.vue";
    import SignInSheet from "../Components/Calendar/SignInSheet.vue";
    import SignInModal from "../Components/Calendar/SignInModal.vue";
    import SpaceSheet from "../Components/Calendar/SpaceSheet.vue";
    import { formatDateTime } from "../Helpers/DateHelper.js";
    import { bookingReturnSlot } from "../Helpers/BookingReturn.js";
    import DangerButton from "../InertiaComponents/DangerButton.vue";
    import DialogModal from "../InertiaComponents/DialogModal.vue";
    import { DateTime } from "luxon";
    import { computed, onMounted, ref } from "vue";
    import { useMediaQuery } from "@vueuse/core";
    import {
        freeRunFrom,
        minimumHours,
        overrideForDay,
        slotsForDay,
    } from "../Composables/useBookingSchedule.js";

    const props = defineProps({
        auth: Object,
        spaces: Array,
        opening_times: Object,
        opening_time_overrides: Object,
        errors: Object,
        events: Object,
        computedCalendarView: Object,
    });

    const viewSpaceDetails = ref(0);

    // The grid used to print this under every column heading, six times over. It belongs here,
    // where it is said once and there is room to say it in words.
    const spaceMinimumLabel = computed(() => {
        const minimum = minimumHours(viewSpaceDetails.value || null);

        return minimum > 1 ? `Booked in blocks of ${minimum} hours or more` : "";
    });
    const selectedTime = ref(null);
    const selectedSpace = ref(null);
    const selectingDatesModal = ref(false);
    const showSignIn = ref(false);
    const amountOfHours = ref(1);

    const closeSelectingDatesModal = () => {
        selectingDatesModal.value = false;
        selectedTime.value = null;
        selectedSpace.value = null;
        amountOfHours.value = 1; // Reset to 1, will be set correctly when opening again
    };

    //
    // DAY & HOURS
    //

    const currentDay = ref(DateTime.now());

    const changeCurrentDay = (amount) => {
        currentDay.value = amount;
    };

    const defineHours = computed(() =>
        slotsForDay(currentDay.value, props.opening_times, props.opening_time_overrides),
    );

    const currentDayOverride = computed(() => overrideForDay(currentDay.value, props.opening_time_overrides));

    const purchaseBlock = ($event, block, space) => {
        if (!props.auth.logged_in) {
            // The redirect threw away the slot the visitor had just chosen. Ask instead, and hold
            // on to it — a phone in a sheet, a desktop in a modal, the same question either way.
            selectedTime.value = block;
            selectedSpace.value = space;
            showSignIn.value = true;
            return;
        }

        selectedTime.value = block;
        selectedSpace.value = space;
        selectingDatesModal.value = true;
        // Start at the minimum bookable hours for this space
        amountOfHours.value = minimumHours(space);
    };

    const closeSignInSheet = () => {
        showSignIn.value = false;
        selectedTime.value = null;
        selectedSpace.value = null;
    };

    const selectedDateTime = (addHours = 0) => {
        return currentDay.value
            .set({ hour: selectedTime.value + addHours })
            .set({ minute: 0 })
            .set({ seconds: 0 });
    };

    /** The bracket price for a given length, or false when no bracket covers it. */
    const priceForHours = (hours) => {
        for (let bracket of selectedSpace.value?.brackets || []) {
            if (hours >= bracket.min && hours <= bracket.max) {
                return bracket.price;
            }
        }

        return false;
    };

    const calculateBookingPrice = () => priceForHours(amountOfHours.value);

    /** Longest run the sheet may offer: whatever is free before the next block or closing. */
    const maxBookableHours = computed(() => {
        if (!selectedSpace.value || selectedTime.value === null) {
            return 1;
        }

        const spaceView = computedCalendarViewFor(selectedSpace.value);

        const run = freeRunFrom({
            day: currentDay.value,
            spaceView: spaceView,
            events: props.events,
            hours: defineHours.value,
            startHour: selectedTime.value,
        });

        return Math.max(minimumHours(selectedSpace.value), run);
    });

    const computedCalendarViewFor = (space) =>
        (props.computedCalendarView || []).find((view) => view.space.id === space.id) || null;

    const setBookingHours = (hours) => {
        const minimum = minimumHours(selectedSpace.value);

        amountOfHours.value = Math.min(Math.max(hours, minimum), maxBookableHours.value);
    };

    const isMobile = useMediaQuery("(max-width: 767.98px)");

    const canAddMoreHours = () => {
        const tempNewHours = amountOfHours.value + 1;
        const lastPossibleTime = getLastPossibleTime();
        const minimum = minimumHours(selectedSpace.value);
        return tempNewHours >= minimum && selectedTime.value + tempNewHours <= lastPossibleTime;
    };

    const addHoursToBooking = (time) => {
        const tempNewHours = amountOfHours.value + time;
        const lastPossibleTime = getLastPossibleTime();
        const minimum = minimumHours(selectedSpace.value);

        // Allow booking as long as the END time is within or equal to the last possible time
        // and we're at or above the minimum hours for this space
        if (tempNewHours >= minimum && selectedTime.value + tempNewHours <= lastPossibleTime) {
            amountOfHours.value = tempNewHours;
        }
    };

    /**
     * The hour a booking starting at selectedTime must end by.
     *
     * This used to hunt through props.orders for the next block, keyed by a date string it
     * then read with DateTime.fromSQL. The keys are ISO timestamps, so every parse came back
     * invalid, every comparison failed, and the function always fell through to closing time
     * — letting the picker offer hours that were already booked. The schedule already knows
     * where the next block is, so ask it.
     */
    const getLastPossibleTime = () => {
        if (!defineHours.value || defineHours.value.length === 0 || !selectedSpace.value) {
            return 0;
        }

        return selectedTime.value + maxBookableHours.value;
    };

    /**
     * Signing in is a full page navigation, so the slot comes back in the URL rather than in
     * memory. Reopen it only if it is still there to book — somebody else may have taken it while
     * the visitor was typing a password, and a table with a minimum needs the whole run free.
     */
    onMounted(() => {
        const slot = bookingReturnSlot(window.location.search);

        if (!slot || !props.auth.logged_in) {
            return;
        }

        // Clear it first, so refreshing does not reopen a sheet the visitor has already dismissed.
        window.history.replaceState({}, "", window.location.pathname);

        const space = (props.spaces || []).find((candidate) => candidate.id === slot.spaceId);

        if (!space) {
            return;
        }

        currentDay.value = slot.day;

        const run = freeRunFrom({
            day: currentDay.value,
            spaceView: computedCalendarViewFor(space),
            events: props.events,
            hours: defineHours.value,
            startHour: slot.hour,
        });

        if (run >= minimumHours(space)) {
            purchaseBlock(null, slot.hour, space);
        }
    });

    const checkout = () => {
        const urlData = {
            startTime: selectedTime.value,
            duration: amountOfHours.value,
            space: selectedSpace.value.id,
            date: selectedDateTime().toISO(),
        };

        window.location.href = "/checkout?" + new URLSearchParams(urlData).toString();
    };
</script>

<style scoped></style>
